import { usePrivateMutation as useMutation } from "@/lib/query/core/use-private-mutation";
import { useQueryClient } from "@tanstack/react-query";
import type { StoreSubscriptionPaymentView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateAdminStoreSubscriptions } from "@/lib/query/core/invalidation";

type ConfirmInput = {
  storeId: string;
  paymentId: string;
};

type RejectInput = ConfirmInput & {
  reason?: string;
};

/**
 * Confirming a seller's top-up is the only path that turns their request into
 * money: the backend writes the wallet credit and runs settlement in one
 * transaction, so held orders unlock oldest-first before this resolves. The
 * store-subscription queries are invalidated afterwards because the wallet
 * balance, the charge ledger and the masked-order count all just moved.
 */
export function useConfirmStoreSubscriptionTopUpMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ storeId, paymentId }: ConfirmInput) =>
      apiJsonClient<StoreSubscriptionPaymentView>(
        endpoints.admin.storeSubscriptionTopUpConfirm(storeId, paymentId),
        { method: "POST" },
      ),
    onSuccess: async (_data, variables) => {
      await invalidateAdminStoreSubscriptions(queryClient, variables.storeId);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}

/** Rejecting moves no money; the proof and the reason stay on the row. */
export function useRejectStoreSubscriptionTopUpMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ storeId, paymentId, reason }: RejectInput) =>
      apiJsonClient<StoreSubscriptionPaymentView>(
        endpoints.admin.storeSubscriptionTopUpReject(storeId, paymentId),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ reason }),
        },
      ),
    onSuccess: async (_data, variables) => {
      await invalidateAdminStoreSubscriptions(queryClient, variables.storeId);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
